/**
 * Phase 7 booking guards — Duffel booking/payment is not implemented.
 * LDS proceeds only when server booking context is available (token) or legacy rawDeal.
 */

/** User-facing Hebrew when Duffel Create Order / LDS draft-payment is not available yet. */
export const DUFFEL_BOOKING_NOT_ENABLED_HE =
  "הזמנה ותשלום עבור טיסה זו עדיין לא זמינים. בחרו הצעה מספק אחר, או נסו שוב מאוחר יותר.";

/**
 * @param {unknown} offer — adapted FE flight offer
 * @returns {boolean}
 */
export function isUnifiedDuffelOffer(offer) {
  if (!offer || typeof offer !== "object") return false;
  const p = String(offer.provider || offer.unified?.provider || "").toLowerCase();
  return p === "duffel";
}

/**
 * @param {unknown} offer
 * @returns {boolean}
 */
export function isUnifiedFlightOffer(offer) {
  if (!offer || typeof offer !== "object") return false;
  return (
    offer.source === "unified" ||
    offer.__unified === true ||
    Boolean(offer.unified) ||
    Boolean(offer.provider && offer.providerOfferId && offer.itineraryIdentity)
  );
}

/**
 * @param {unknown} offer
 * @returns {boolean}
 */
export function isExpiredUnifiedOffer(offer) {
  if (!offer || typeof offer !== "object") return false;
  if (offer.isExpired === true) return true;
  const at = offer.providerOfferExpiresAt;
  if (!at) return false;
  const t = Date.parse(String(at));
  if (!Number.isFinite(t)) return false;
  return t <= Date.now();
}

/**
 * Opaque token from unified search bookingContext (never invent).
 * @param {unknown} offer
 * @returns {string|null}
 */
export function getBookingContextToken(offer) {
  if (!offer || typeof offer !== "object") return null;
  const fromOffer =
    offer.bookingContext?.bookingContextToken ||
    offer.unified?.bookingContext?.bookingContextToken ||
    offer.bookingContextToken;
  if (fromOffer != null && String(fromOffer).trim()) {
    return String(fromOffer).trim();
  }
  return null;
}

/**
 * Whether server advertised a resolvable LDS booking context.
 * @param {unknown} offer
 * @returns {boolean}
 */
export function hasLdsBookingContext(offer) {
  if (!offer || typeof offer !== "object") return false;
  if (offer.rawDeal) return true; // legacy path
  const ctx =
    offer.bookingContext ||
    offer.unified?.bookingContext ||
    null;
  if (ctx && ctx.available === true && getBookingContextToken(offer)) {
    return true;
  }
  return false;
}

/** Shared Hebrew when selection cannot continue from results. */
export const FLIGHT_CONTINUE_BLOCKED_HE =
  "לא ניתן להמשיך להזמנה עבור טיסה זו";

/**
 * Provider-native amount present on an adapted unified card offer.
 * @param {unknown} offer
 * @returns {boolean}
 */
export function hasUnifiedProviderPrice(offer) {
  if (!offer || typeof offer !== "object") return false;
  const amount = Number(
    offer.providerPriceAmount ??
      offer.price?.amount ??
      offer.rawTotalFare ??
      offer.totalFare
  );
  const currency = String(
    offer.providerPriceCurrency ||
      offer.price?.currency ||
      offer.rawCurrency ||
      ""
  ).trim();
  return Number.isFinite(amount) && amount > 0 && currency !== "";
}

/**
 * Whether results "Continue to booking" may proceed for this card selection.
 * - Unified Duffel: allow when provider amount+currency are present (no LDS rawDeal).
 * - LDS / legacy: unchanged — rawDeal or booking context via canEnterLdsBookingFlow.
 *
 * Does NOT authorize Duffel Create Order / payment (see canEnterLdsBookingFlow).
 *
 * @param {unknown} offer — adapted FE flight offer
 * @returns {{ ok: boolean, reason?: string, messageHe?: string, needsResolve?: boolean }}
 */
export function canContinueFlightOfferSelection(offer) {
  if (!offer || typeof offer !== "object") {
    return {
      ok: false,
      reason: "missing_offer",
      messageHe: FLIGHT_CONTINUE_BLOCKED_HE,
    };
  }

  if (isExpiredUnifiedOffer(offer)) {
    return {
      ok: false,
      reason: "expired",
      messageHe: "ההצעה פגה — יש לבצע חיפוש מחדש",
    };
  }

  if (isUnifiedDuffelOffer(offer)) {
    if (hasUnifiedProviderPrice(offer)) {
      return { ok: true, reason: "duffel_provider_price" };
    }
    return {
      ok: false,
      reason: "duffel_price_incomplete",
      messageHe: FLIGHT_CONTINUE_BLOCKED_HE,
    };
  }

  // Legacy LDS deal attached directly on the card
  if (offer.rawDeal) {
    return { ok: true, reason: "lds_raw_deal" };
  }

  return canEnterLdsBookingFlow(offer);
}

/**
 * Whether the current LDS booking/payment flow may proceed for this selection.
 * @param {unknown} offer
 * @returns {{ ok: boolean, reason?: string, messageHe?: string, needsResolve?: boolean }}
 */
export function canEnterLdsBookingFlow(offer) {
  if (!offer || typeof offer !== "object") {
    return {
      ok: false,
      reason: "missing_offer",
      messageHe: FLIGHT_CONTINUE_BLOCKED_HE,
    };
  }

  if (isExpiredUnifiedOffer(offer)) {
    return {
      ok: false,
      reason: "expired",
      messageHe: "ההצעה פגה — יש לבצע חיפוש מחדש",
    };
  }

  if (isUnifiedDuffelOffer(offer)) {
    return {
      ok: false,
      reason: "duffel_booking_not_enabled",
      messageHe: DUFFEL_BOOKING_NOT_ENABLED_HE,
    };
  }

  const provider = String(offer.provider || "lds").toLowerCase();
  if (provider === "lds" || !offer.provider) {
    if (offer.rawDeal) {
      return { ok: true };
    }
    if (hasLdsBookingContext(offer)) {
      return { ok: true, needsResolve: true };
    }
    return {
      ok: false,
      reason: "lds_booking_context_unavailable",
      messageHe: FLIGHT_CONTINUE_BLOCKED_HE,
    };
  }

  return {
    ok: false,
    reason: "unsupported_provider",
    messageHe: DUFFEL_BOOKING_NOT_ENABLED_HE,
  };
}


/**
 * Duffel must never enter LDS booking helpers (rawDeal / buildSelectedResultForLdsFlightBooking).
 * @param {unknown} offer
 * @returns {boolean}
 */
export function shouldSkipLdsBookingHelpers(offer) {
  if (!offer || typeof offer !== "object") return true;
  if (isUnifiedDuffelOffer(offer)) return true;
  if (offer.duffelResolved === true && String(offer.provider || "").toLowerCase() === "duffel") {
    return true;
  }
  return false;
}

/**
 * After Phase 9 resolve, Duffel may advance past selection for inspection only.
 * Payment / LDS booking remains blocked via canEnterLdsBookingFlow.
 * @param {unknown} offer
 * @returns {{ ok: boolean, reason?: string, messageHe?: string }}
 */
export function canAdvanceAfterDuffelResolve(offer) {
  if (!offer || typeof offer !== "object") {
    return { ok: false, reason: "missing_offer", messageHe: "לא ניתן להמשיך עם טיסה זו" };
  }
  if (!isUnifiedDuffelOffer(offer)) {
    return { ok: false, reason: "not_duffel" };
  }
  if (isExpiredUnifiedOffer(offer) || offer.isExpired === true) {
    return {
      ok: false,
      reason: "expired",
      messageHe: "הטיסה אינה זמינה יותר. אנא בחרו טיסה אחרת.",
    };
  }
  if (offer.duffelResolved !== true) {
    return {
      ok: false,
      reason: "resolve_required",
      messageHe: "יש לאשר זמינות ומחיר לפני המשך",
    };
  }
  if (!getBookingContextToken(offer)) {
    return {
      ok: false,
      reason: "booking_context_missing",
      messageHe: "תוקף הבחירה פג. אנא בצעו חיפוש מחדש ובחרו טיסה.",
    };
  }
  return { ok: true };
}
